import React, { useState, useEffect } from "react";
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import { Divider } from "@mui/material";
import Chip from '@mui/material/Chip';
import {
  setThrottlingLimit,
  updateApiKey,
  fetchRestApis,
  fetchApiKeys,
} from "services/custom-features";
import Loader from "components/Loader";

const ApiThrottling = () => {
  const [totalApiGateways, setTotalApiGateways] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [totalStages, setTotalStages] = useState([]);
  const [stageEndpoints, setStageEndpoints] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSelectedApiKey, setIsSelectedApiKey] = useState(true);
  const [restApis, setRestApi] = useState([]);
  const [selectedRestApi, setSelectedRestApi] = useState([]);
  const [totalUsagePlans, setTotalUsagePlans] = useState([]);
  const [totalApiKeys, setTotalApiKeys] = useState([]);

  const [formData, setFormData] = useState({
    rate: "",
    burst: "",
    stage: "",
    resources: [],
    gatewayApi: "",
    usagePlan: "",
  });

  const [notificationResponse, setNotificationResponse] = useState({
    responseType: "",
    showMessage: false,
    message: "",
  });

  const handleStateChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const setGatewayApis = (restApis) => {
    const apiGatewayOptions = restApis.map((item) => ({
      key: item.apiId,
      value: item.apiName,
      text: item.apiName,
    }));
    setTotalApiGateways(apiGatewayOptions);
  };

  const setApiKeys = async (apiKeys) => {
    const totalApiKeys = apiKeys.map((item) => ({
      key: item.id,
      value: item.name,
      text: item.name,
    }));
    setTotalApiKeys(totalApiKeys);
  };

  useEffect(() => {
    async function fetchData() {
      const restApis = await fetchRestApis();
      const apiKeys = await fetchApiKeys();
      setRestApi(restApis.data);
      setGatewayApis(restApis.data);
      setApiKeys(apiKeys.data);
    }
    fetchData();
  }, []);

  const handleUpdateApiThrottling = async (e) => {
    e.preventDefault();

    const { rate, burst, stage, resources, gatewayApi, usagePlan } =
      formData;
    const newErrors = {};

    if (!rate) newErrors.rate = "API rate is required";

    if (!burst) newErrors.burst = "API burst is required";

    if (!stage) newErrors.stage = "API stage is required";

    if (!resources || resources.length === 0) newErrors.resources = "API resources are required";

    if (!gatewayApi) newErrors.env = "Gateway API  is required";

    if (!usagePlan) newErrors.usagePlan = "Usage Plan is required";

    if (Object.keys(newErrors).length === 0) {
      let response;

      const usagePlanId = selectedRestApi[0].usagePlan.filter(
        (item) => item.name === usagePlan
      )[0].id;
      const apiId = selectedRestApi[0].apiId;

      try {
        response = await setThrottlingLimit({
          apiId,
          stage,
          resources,
          rate,
          burst,
          usagePlanId,
        });
      } catch (error) {
        console.log("Error in method Throttling");
      }

      setNotificationResponse({
        status: response.status,
        showMessage: true,
        message: response.message,
      });

      setFormData({
        rate: "",
        burst: "",
        stage: "",
        resources: [],
        gatewayApi: "",
        usagePlan: "",
      });
      setErrors({});
    } else {
      // Display validation errors
      setErrors(newErrors);
    }
  };

  const handleUpdateApiKey = async (e) => {
    try {
      e.preventDefault();

      if (!apiKey) setIsSelectedApiKey(false);
      else {
        const apiKeyId = totalApiKeys.find((item) => item.value === apiKey).key;
        const response = await updateApiKey(apiKeyId);
        setNotificationResponse({
          status: response.status,
          showMessage: true,
          message: response.message,
        });
        setApiKey("");
        setIsSelectedApiKey(true);
      }
    } catch (error) {
      setNotificationResponse({
        status: 500,
        showMessage: true,
        message: error.message,
      });
      setApiKey("");
      setIsSelectedApiKey(true);
    }
  };

  const handleStageChange = async (e, { value }) => {
    e.preventDefault();

    const resourcesWithMethods = selectedRestApi[0].resources.flatMap(resource =>
      resource.method.map(method => ({
        key: `${resource.id}-${method}`,
        value: `${resource.name} | ${method}`,
        text: `${resource.name} | ${method}`
      }))
    );

    setStageEndpoints(resourcesWithMethods);
    setFormData({ ...formData, stage: value });
  };

  const handleEndpointsChange = async (e, { key, value }) => {
    e.preventDefault();
    setFormData({ ...formData, resources: value });
  };

  const handleRestApiChange = async (e, { value }) => {
    const selectedApi = restApis.filter((item) => item.apiName === value);

    const stages = selectedApi[0].stages.map((item) => ({
      key: item,
      value: item,
      text: item,
    }));

    const usagePlans = selectedApi[0].usagePlan.map((item) => ({
      key: item.id,
      value: item.name,
      text: item.name,
    }));

    setTotalUsagePlans(usagePlans);
    setSelectedRestApi(selectedApi);
    setTotalStages(stages);
    setFormData({ 
      ...formData, 
      gatewayApi: value,
      usagePlan: '', 
      stage: '', 
      resources: [], 
      rate: '', 
      burst: '' 
    });
  };

  const handleApiKeyChange = async (e, { value }) => {
    e.preventDefault();
    setApiKey(value);
  };

  const handleDismiss = () => {
    setNotificationResponse({
      status: "",
      showMessage: false,
      message: "",
    });
  };

  return (
    <>
      {notificationResponse.showMessage ? (
        <Alert severity={notificationResponse.status === 200 ? 'success' : 'error'} onClose={handleDismiss}>{notificationResponse.message}</Alert>
      ) : null}

      {restApis.length ? (
        <Container disableGutters maxWidth={false} sx={{ p: '1em 5em', display: 'flex', justifyContent: 'space-between' }}>
          <Box component="form" onSubmit={handleUpdateApiThrottling} sx={{ width: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">API Throttling</Typography>
            <Divider />
            <FormControl size="small" required error={!!errors.env}>
              <InputLabel id="gateway-apis-label">Gateway APIs</InputLabel>
              <Select labelId="gateway-apis-label" value={formData.gatewayApi} label="Gateway APIs" onChange={(e) => handleRestApiChange(e, { value: e.target.value })}>
                {totalApiGateways.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.text}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" required  error={!!errors.usagePlan}>
              <InputLabel id="usage-plan-label">Usage Plan</InputLabel>
              <Select labelId="usage-plan-label" value={formData.usagePlan} label="Usage Plan" onChange={(e) => handleStateChange('usagePlan', e.target.value)}>
                {totalUsagePlans.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.text}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" required error={!!errors.stage}>
              <InputLabel required id="stage-label">Stage</InputLabel>
              <Select labelId="stage-label" value={formData.stage} label="Stage" onChange={(e) => handleStageChange(e, { value: e.target.value })}>
                {totalStages.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.text}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" required error={!!errors.resources}>
              <InputLabel id="resources-label">Resources</InputLabel>
              <Select labelId="resources-label" multiple label="Resources" value={formData.resources} onChange={(e) => handleEndpointsChange(e, { value: e.target.value })}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}>
                {stageEndpoints.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.text}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Rate" value={formData.rate} placeholder="100" onChange={(e) => handleStateChange('rate', e.target.value)} error={!!errors.rate} helperText={errors.rate} />
            <TextField size="small" label="Burst" value={formData.burst} placeholder="200" onChange={(e) => handleStateChange('burst', e.target.value)} error={!!errors.burst}  helperText={errors.burst} />
            <Button disableElevation type="submit" variant="contained" sx={{ width: '6rem' }}>Save</Button>
          </Box>

          <Box component="form" onSubmit={handleUpdateApiKey} sx={{ width: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Update API key</Typography>
            <Divider />
            <FormControl size="small" required error={!isSelectedApiKey}>
              <InputLabel id="api-keys-label">API Keys</InputLabel>
              <Select labelId="api-keys-label" value={apiKey} label="API Keys" onChange={(e) => handleApiKeyChange(e, { value: e.target.value })}>
                {totalApiKeys.map(opt => <MenuItem key={opt.key} value={opt.value}>{opt.text}</MenuItem>)}
              </Select>
            </FormControl>
            <Button type="submit" disableElevation variant="contained" sx={{ width: '6rem' }}>Update</Button>
          </Box>
        </Container>
      ) : (
        <Loader />
      )}
    </>
  );
};
export default ApiThrottling;
